import React, { useState, useCallback, memo, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Circle } from '@react-google-maps/api';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';
import ScannerCircle from './ScannerCircle';

const containerStyle = {
  flex: 1,
  height: '100%',
};

const defaultCenter = {
  lat: 40.4168,
  lng: -3.7038 // Madrid
};

const mapOptions = {
  styles: [
    {
      "elementType": "geometry",
      "stylers": [{ "color": "#010A16" }]
    },
    {
      "elementType": "labels.text.fill",
      "stylers": [{ "color": "#746855" }]
    },
    {
      "elementType": "labels.text.stroke",
      "stylers": [{ "color": "#242f3e" }]
    },
    {
      "featureType": "administrative.locality",
      "elementType": "labels.text.fill",
      "stylers": [{ "color": "#d59563" }]
    },
    {
      "featureType": "poi",
      "elementType": "labels.text.fill",
      "stylers": [{ "color": "#d59563" }]
    },
    {
      "featureType": "poi.park",
      "elementType": "geometry",
      "stylers": [{ "color": "#263c3f" }]
    },
    {
      "featureType": "poi.park",
      "elementType": "labels.text.fill",
      "stylers": [{ "color": "#6b9a76" }]
    },
    {
      "featureType": "road",
      "elementType": "geometry",
      "stylers": [{ "color": "#38414e" }]
    },
    {
      "featureType": "road",
      "elementType": "geometry.stroke",
      "stylers": [{ "color": "#212a37" }]
    },
    {
      "featureType": "road",
      "elementType": "labels.text.fill",
      "stylers": [{ "color": "#9ca5b3" }]
    },
    {
      "featureType": "road.highway",
      "elementType": "geometry",
      "stylers": [{ "color": "#746855" }]
    },
    {
      "featureType": "road.highway",
      "elementType": "geometry.stroke",
      "stylers": [{ "color": "#1f2835" }]
    },
    {
      "featureType": "road.highway",
      "elementType": "labels.text.fill",
      "stylers": [{ "color": "#f3d19c" }]
    },
    {
      "featureType": "transit",
      "elementType": "geometry",
      "stylers": [{ "color": "#2f3948" }]
    },
    {
      "featureType": "transit.station",
      "elementType": "labels.text.fill",
      "stylers": [{ "color": "#d59563" }]
    },
    {
      "featureType": "water",
      "elementType": "geometry",
      "stylers": [{ "color": "#17263c" }]
    },
    {
      "featureType": "water",
      "elementType": "labels.text.fill",
      "stylers": [{ "color": "#515c6d" }]
    },
    {
      "featureType": "water",
      "elementType": "labels.text.stroke",
      "stylers": [{ "color": "#17263c" }]
    }
  ],
  disableDefaultUI: false,
  zoomControl: true,
  disableDoubleClickZoom: true,
};

// Brand color palette by source
// Esquema de color por marca:
//   Burger King = ÚNICO rojo · Popeyes = naranja fuerte · McDonald's = verde ·
//   KFC = blanco · el resto = variantes de azul (se distinguen además por su letra).
// Debe coincidir con BRAND_KML_COLORS de apps/api/src/controllers/exportController.js.
export const BRAND_COLORS = {
  bk:        '#E4002B', // rojo (el único)
  popeye:    '#FF6600', // naranja fuerte
  mcdonalds: '#009639', // verde
  kfc:       '#FFFFFF', // blanco
  // Variantes de azul
  carrefour: '#003DA5',
  goiko:     '#00A3E0',
  papajohns: '#1565C0',
  vips:      '#2962FF',
  fosters:   '#3F51B5',
  fiveguys:  '#00B0FF',
  pizzahut:  '#0277BD',
  pans:      '#00ACC1',
  carlsjr:   '#5C6BC0',
  rodilla:   '#0288D1',
  vicio:     '#0097A7',
  tgb:       '#4A90D9',
  tacobell:  '#3949AB',
  dominos:   '#006491',
  wingstop:  '#039BE5',
  jollibee:  '#4FC3F7',
  // Fuentes no-marca
  osm_turbo: '#00CEC9',
  turbo:     '#00CEC9',
  manual:    '#0B3D91',
};

// Abreviatura de marca (2-3 letras) para el icono circular. Debe coincidir con
// BRAND_LETTER de apps/api/src/controllers/exportController.js.
const BRAND_LETTER = {
  mcdonalds: 'McD',
  kfc:       'KFC',
  popeye:    'POP',
  carrefour: 'CRF',
  bk:        'BK',
  osm_turbo: '◎',
  turbo:     '◎',
  manual:    '★',
  goiko:     'GK',
  papajohns: 'PJ',
  vips:      'VIP',
  fosters:   'FH',
  fiveguys:  '5G',
  pizzahut:  'PH',
  pans:      'PAN',
  carlsjr:   'CJ',
  rodilla:   'ROD',
  vicio:     'VIC',
  tgb:       'TGB',
  tacobell:  'TB',
  dominos:   'DOM',
  wingstop:  'WS',
  jollibee:  'JOL',
};

// Luminancia relativa de un color hex (#RGB o #RRGGBB): true si es "claro".
const isLightColor = (hex) => {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (full.length !== 6) return false;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7;
};

const makeSvgMarker = (color, letter = '') => {
  // La fuente se reduce con 2-3 caracteres (McD, TGB…) para que quepan en el círculo.
  const len = String(letter).length;
  const fontSize = len >= 3 ? 8 : len === 2 ? 10 : 13;
  const baselineY = len >= 3 ? 18 : 20;
  // Sobre un relleno claro (KFC blanco) el borde y la letra van en oscuro para
  // que se vean; sobre relleno oscuro, en blanco.
  const light = isLightColor(color);
  const ink = light ? '#333333' : '#ffffff';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30"><circle cx="15" cy="15" r="12" fill="${color}" stroke="${ink}" stroke-width="2.5"/>${letter ? `<text x="15" y="${baselineY}" text-anchor="middle" fill="${ink}" font-size="${fontSize}" font-family="Arial,sans-serif" font-weight="bold">${letter}</text>` : ''}</svg>`;
  return {
      url: `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`,
    scaledSize: new window.google.maps.Size(30, 30),
    anchor: new window.google.maps.Point(15, 15),
  };
};

const getMarkerIcon = (google, opp) => {
  if (!window.google) return null;

  // Para marcas conocidas manda el color canónico (así el nuevo esquema se aplica
  // también a datos ya importados); para fuentes sin color propio, el de visuals.
  const source = opp.source || '';
  const color = BRAND_COLORS[source] || opp.visuals?.color || '#0B3D91';

  // An explicit letter (set per brand) always wins and is drawn inside the circle,
  // overriding any shape icon so the label stays visible.
  const customLetter = (opp.visuals?.letter || '').trim();
  if (customLetter) {
    return makeSvgMarker(color, customLetter);
  }

  const letter = opp.visuals?.icon ? '' : (BRAND_LETTER[source] || '');

  // If a custom SVG path icon type is set, fall back to old symbol approach
  const type = opp.visuals?.icon;
  if (type && type !== 'pushpin' && type !== 'dot') {
    let path = window.google.maps.SymbolPath.CIRCLE;
    let scale = 7;
    if (type === 'target') {
      path = "M 12 2 C 6.47 2 2 6.47 2 12 C 2 17.53 6.47 22 12 22 C 17.53 22 22 17.53 22 12 C 22 6.47 17.53 2 12 2 Z M 12 20 C 7.59 20 4 16.41 4 12 C 4 7.59 7.59 4 12 4 C 16.41 4 20 7.59 20 12 C 20 16.41 16.41 20 12 20 Z M 12 7 C 9.24 7 7 9.24 7 12 C 7 14.76 9.24 17 12 17 C 14.76 17 17 14.76 17 12 C 17 9.24 14.76 7 12 7 Z";
      scale = 1;
    } else if (type === 'hazard') {
      path = "M 12 2 L 1 21 L 23 21 Z"; scale = 1;
    } else if (type === 'store') {
      path = "M 20 4 H 4 v 2 h 16 V 4 z m 1 10 v -2 l -1 -5 H 4 l -1 5 v 2 h 1 v 6 h 10 v -6 h 4 v 6 h 2 v -6 h 1 z m -9 4 H 6 v -4 h 6 v 4 z"; scale = 1;
    } else if (type === 'food') {
      path = "M 11 9 H 9 V 2 H 7 v 7 H 5 V 2 H 3 v 7 c 0 2.12 1.66 3.84 3.75 3.97 V 22 h 2.5 v -9.03 C 11.34 12.84 13 11.12 13 9 V 2 h -2 v 7 z m 5 -3 v 8 h 2.5 v 8 H 21 V 2 c -2.76 0 -5 2.24 -5 4 z"; scale = 1;
    }
    return { path, fillColor: color, fillOpacity: 1, strokeWeight: 1, strokeColor: '#FFFFFF', scale, anchor: new window.google.maps.Point(12, 12) };
  }

  // Default: branded colored circle with letter
  return makeSvgMarker(color, letter);
};

function MapContainer({ 
  onMapClick, 
  onMapDblClick,
  onMarkerClick, 
  onScoutClick, 
  opportunities = [], 
  scoutResults = [],
  viableMarkers = [],
  scanCircle,
  coordinatePoint,
  center,
  zoom,
  style,
  addressToSearch,
  searchTrigger,
  onBoundsChanged,
}) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  });

  const [map, setMap] = useState(null);
  const [geocodedCenter, setGeocodedCenter] = useState(null);
  const [geocodedZoom, setGeocodedZoom] = useState(null);

  const lastProcessedTrigger = useRef(0);
  const clustererRef = useRef(null);
  const markersRef = useRef([]);

  // Clear geocoding override ONLY when a different strategic center is actively selected
  useEffect(() => {
    if (geocodedCenter) {
      setGeocodedCenter(null);
      setGeocodedZoom(null);
    }
  }, [center?.lat, center?.lng, scanCircle?.center?.lat, scanCircle?.center?.lng]);

  // Handle the actual panning visually when geocodedCenter is updated
  useEffect(() => {
    if (geocodedCenter && map) {
      map.panTo(geocodedCenter);
      map.setZoom(16);
    }
  }, [geocodedCenter, map]);

  // Address Geocoding Logic
  useEffect(() => {
    if (isLoaded && addressToSearch && searchTrigger > lastProcessedTrigger.current && window.google) {
      lastProcessedTrigger.current = searchTrigger;
      
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: addressToSearch }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const loc = {
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          };
          setGeocodedCenter(loc);
          setGeocodedZoom(16);
        } else {
          console.error('Geocode failed for target:', addressToSearch, status);
        }
      });
    }
  }, [isLoaded, searchTrigger, addressToSearch]);

  const mapCenter = geocodedCenter || center || (scanCircle?.center) || defaultCenter;
  
  // Dynamic Zoom calculation based on radius
  const getAutoZoom = (radius) => {
    if (radius <= 200) return 18;
    if (radius <= 400) return 17;
    if (radius <= 800) return 16;
    if (radius <= 1500) return 15;
    return 14;
  };
  
  const mapZoom = geocodedZoom || zoom || (scanCircle ? getAutoZoom(scanCircle.radius) : 12);


  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  // Imperatively manage MarkerClusterer for opportunity markers
  useEffect(() => {
    if (!map || !window.google) return;

    // Always destroy previous clusterer and markers cleanly
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (opportunities.length === 0) return;

    // Skip opportunities without a resolved location (created address-only).
    const locatable = opportunities.filter((opp) => opp.location?.coordinates?.length === 2);
    if (locatable.length === 0) return;

    const newMarkers = locatable.map((opp) => {
      const marker = new window.google.maps.Marker({
        position: {
          lat: opp.location.coordinates[1],
          lng: opp.location.coordinates[0],
        },
        title: opp.nameWithCount || opp.name,
        icon: getMarkerIcon(window.google, opp),
      });
      marker.addListener('click', () => {
        if (onMarkerClick) onMarkerClick(opp);
      });
      return marker;
    });

    markersRef.current = newMarkers;

    // Clusters at zoom ≤ 14, individual pins at zoom > 14
    clustererRef.current = new MarkerClusterer({
      map,
      markers: newMarkers,
      algorithm: new GridAlgorithm({ maxZoom: 14, gridSize: 60 }),
    });
  }, [map, opportunities, onMarkerClick]);

  // Cleanup clusterer on unmount
  useEffect(() => {
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
    };
  }, []);

  const onUnmount = useCallback(function callback(map) {
    setMap(null);
  }, []);

  const handleClick = (e) => {
    if (onMapClick) {
      onMapClick({
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      });
    }
  };

  const handleDblClick = (e) => {
    if (onMapDblClick) {
      // Pass pixel position so parent can anchor the context menu
      const domEvent = e.domEvent || {};
      onMapDblClick({
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
        x: domEvent.clientX || 0,
        y: domEvent.clientY || 0,
      });
    }
  };

  const handleBoundsChanged = () => {
    if (map && onBoundsChanged) {
      onBoundsChanged(map.getBounds());
    }
  };

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={style || containerStyle}
      center={mapCenter}
      zoom={mapZoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={handleClick}
      onDblClick={handleDblClick}
      onBoundsChanged={handleBoundsChanged}
      options={mapOptions}
    >
      {/* Opportunity markers are managed imperatively via MarkerClusterer (see useEffect above) */}

      {/* Viable Opportunity Markers + Zones (from Find Opportunity scan) */}
      {viableMarkers.map((v, i) => {
        const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="13" fill="#00b894" stroke="#ffffff" stroke-width="2.5" opacity="0.95"/><text x="16" y="21" text-anchor="middle" fill="white" font-size="14" font-family="Arial,sans-serif" font-weight="bold">✓</text></svg>`;
        const svgIcon = {
          url: `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgStr)))}`,
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 16),
        };
        const center = { lat: v.lat, lng: v.lng };
        return (
          <React.Fragment key={`viable-${i}`}>
            {/* Scan radius — where engines+competence were found */}
            <Circle
              center={center}
              radius={v.radius || 500}
              options={{
                strokeColor: '#00b894',
                strokeOpacity: 0.7,
                strokeWeight: 1.5,
                fillColor: '#00b894',
                fillOpacity: 0.08,
                clickable: false,
                zIndex: 2,
              }}
            />
            {/* Safe area — exclusion zone for bases */}
            {v.safeArea && (
              <Circle
                center={center}
                radius={v.safeArea}
                options={{
                  strokeColor: '#E27B58',
                  strokeOpacity: 0.8,
                  strokeWeight: 1.5,
                  strokeDashArray: '6 4',
                  fillColor: '#E27B58',
                  fillOpacity: 0.06,
                  clickable: false,
                  zIndex: 3,
                }}
              />
            )}
            <Marker
              position={center}
              title={`VIABLE: ${v.brand || v.name || 'Zone'} — E:${v.engineCount} C:${v.competenceCount}`}
              icon={svgIcon}
              zIndex={999}
            />
          </React.Fragment>
        );
      })}

      {/* Scout Results (Tactical Color Coded) */}
      {scoutResults.map((res) => {
        const color = res.color || '#00CEC9';
        const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" fill="${color}" stroke="#ffffff" stroke-width="2.5" opacity="1"/></svg>`;
        const svgIcon = {
          url: `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgStr)))}`,
          scaledSize: new window.google.maps.Size(24, 24),
          anchor: new window.google.maps.Point(12, 12),
        };
        return (
          <Marker
              key={res.id}
            position={{ lat: res.lat, lng: res.lng }}
            title={res.name}
            onClick={() => onScoutClick && onScoutClick(res)}
            icon={svgIcon}
          />
        );
      })}

      {/* Coordinate point marker (dblclick on empty map) */}
      {coordinatePoint && (
        <Marker
          position={{ lat: coordinatePoint.lat, lng: coordinatePoint.lng }}
          title={`${coordinatePoint.lat.toFixed(5)}, ${coordinatePoint.lng.toFixed(5)}`}
          icon={(() => {
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34"><circle cx="17" cy="17" r="13" fill="#E27B58" stroke="#ffffff" stroke-width="2.5"/><text x="17" y="22" text-anchor="middle" fill="white" font-size="15" font-family="Arial,sans-serif" font-weight="bold">✦</text></svg>`;
            return {
              url: `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`,
              scaledSize: new window.google.maps.Size(34, 34),
              anchor: new window.google.maps.Point(17, 17),
            };
          })()}
          zIndex={1000}
        />
      )}

      {/* Area Scanner Range Visualization */}
      {scanCircle && (
        <ScannerCircle 
          center={scanCircle.center}
          radius={scanCircle.radius}
          visible={scanCircle.visible}
        />
      )}
    </GoogleMap>
  ) : (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100%', 
      backgroundColor: '#05070A',
      color: '#0B3D91' 
    }}>
      <div className="nasa-loader">INITIALIZING SPATIAL ANALYTICS...</div>
    </div>
  );
}

export default memo(MapContainer);
